import { useState } from 'react';
import { useNavigate } from 'react-router';

import { Button } from '@ds/core';
import { Alert, Modal } from '@ds/feedback';
import { FormField, Textarea } from '@ds/forms';
import { useAviso } from '@/comuns/toast';
import { iniciarImpersonacao } from '@/session/sessao';

/**
 * Entrar numa clínica.
 *
 * O motivo é pedido **antes**, aqui, e não depois de já estar dentro: começar
 * uma impersonação é uma escrita, e escrita não acontece por navegação. Quando
 * ela abre, aí sim a URL `/admin/impersonar/…` passa a valer — a guarda só deixa
 * entrar no surface da clínica porque a sessão já carrega a impersonação viva.
 *
 * O `:usuarioId` da rota vem do protótipo, que entrava "como" alguém. Este
 * produto não faz isso: entrar como outra pessoa atribuiria a ela o que o
 * operador fizesse. Entra-se na CLÍNICA, em leitura, com o próprio crachá.
 */
export function ImpersonarModal({ clinicaId, clinica, aberto, onFechar }: {
    clinicaId: string;
    clinica: string;
    aberto: boolean;
    onFechar: () => void;
}) {
    const navigate = useNavigate();
    const aviso = useAviso();
    const [motivo, setMotivo] = useState('');
    const [entrando, setEntrando] = useState(false);

    const entrar = async () => {
        setEntrando(true);
        try {
            const imp = await iniciarImpersonacao(clinicaId, motivo);
            aviso({
                title: `Você entrou em ${imp.clinica}`,
                description: 'Somente leitura, por 30 minutos, e o acesso ficou registrado.',
                tone: 'warning',
            });
            onFechar();
            navigate(`/admin/impersonar/${clinicaId}/-`, { replace: true });
        } catch (e) {
            aviso({
                title: 'Não foi possível entrar',
                description: e instanceof Error ? e.message : 'Tente de novo.',
                tone: 'danger',
            });
        } finally {
            setEntrando(false);
        }
    };

    return (
        <Modal
            open={aberto}
            title={`Entrar em ${clinica}`}
            onClose={onFechar}
            dirty={motivo.trim() !== ''}
            width={560}
            footer={(
                <>
                    <Button variant="ghost" onClick={onFechar}>Cancelar</Button>
                    <Button onClick={entrar} disabled={motivo.trim().length < 10 || entrando}>
                        {entrando ? 'Entrando…' : 'Entrar em leitura'}
                    </Button>
                </>
            )}
        >
            <div style={{ display: 'flex', flexDirection: 'column', gap: 14 }}>
                <Alert tone="warning" dismissible={false} title="O que este acesso é, e o que não é">
                    É <strong>somente leitura</strong>, dura <strong>30 minutos</strong> e fica registrado na
                    auditoria desta clínica, com o seu nome e o motivo abaixo. Você continua sendo você: não se
                    entra como outra pessoa, porque isso atribuiria a ela o que você fizesse.
                </Alert>

                <FormField
                    label="Motivo" htmlFor="imp-motivo" required
                    hint="Quem entra em prontuário de paciente escreve por quê. Ao menos 10 caracteres."
                >
                    <Textarea
                        id="imp-motivo" value={motivo} onChange={setMotivo} rows={3}
                        placeholder="Chamado #12: a recepção não vê a agenda de quinta."
                    />
                </FormField>
            </div>
        </Modal>
    );
}
